// third-party
import { presetPalettes } from '@ant-design/colors';

// project imports
import ThemeOption from './theme';
import { extendPaletteWithChannels } from 'utils/colorUtils';

const greyAscent = ['#fafafa', '#bfbfbf', '#434343', '#1f1f1f'];

// ==============================|| GREY COLORS BUILDER ||============================== //

function buildGrey() {
  let greyPrimary = [
    '#ffffff',
    '#fafafa',
    '#f5f5f5',
    '#f0f0f0',
    '#d9d9d9',
    '#bfbfbf',
    '#8c8c8c',
    '#595959',
    '#262626',
    '#141414',
    '#000000'
  ];
  let greyConstant = ['#fafafb', '#e6ebf1'];

  return [...greyPrimary, ...greyAscent, ...greyConstant];
}

// ==============================|| DARK GREY COLORS BUILDER ||============================== //
//
// Same slot layout as buildGrey() (0-10 primary, A100/A200/A400/A700 ascent,
// A50 page background, A800 card border) on a navy-tinted dark ramp, so every
// component that reads a grey slot inverts cleanly in dark mode.

function buildDarkGrey() {
  const greyPrimary = [
    '#0e1b30', // 0  → paper surfaces
    '#12203a', // 50
    '#172743', // 100
    '#1d2f4e', // 200 → dividers
    '#27395c', // 300
    '#3a4d73', // 400
    '#61759e', // 500
    '#8fa1c4', // 600 → secondary text
    '#b9c5de', // 700
    '#dae1f1', // 800
    '#eff3fb' // 900
  ];
  const greyAscent = ['#172743', '#3a4d73', '#8fa1c4', '#dae1f1'];
  const greyConstant = ['#0a1424', '#22345a'];
  return [...greyPrimary, ...greyAscent, ...greyConstant];
}

// ==============================|| DEFAULT THEME - PALETTE ||============================== //

export function buildPalette(presetColor) {
  const lightColors = { ...presetPalettes, grey: buildGrey() };
  const lightPaletteColor = ThemeOption(lightColors, presetColor, 'light');

  const darkColors = { ...presetPalettes, grey: buildDarkGrey() };
  const darkPaletteColor = ThemeOption(darkColors, presetColor, 'dark');

  const commonColor = { common: { black: '#000', white: '#fff' } };

  const extendedLight = extendPaletteWithChannels(lightPaletteColor);
  const extendedDark = extendPaletteWithChannels(darkPaletteColor);
  const extendedCommon = extendPaletteWithChannels(commonColor);

  return {
    light: {
      mode: 'light',
      ...extendedCommon,
      ...extendedLight,
      text: {
        // PrivacyPal: Deep Navy for primary text/headlines (never pure black)
        primary: '#01204E',
        secondary: extendedLight.grey[600],
        disabled: extendedLight.grey[400]
      },
      action: { disabled: extendedLight.grey[300] },
      divider: extendedLight.grey[200],
      background: {
        paper: extendedLight.grey[0],
        default: extendedLight.grey.A50
      }
    },
    dark: {
      mode: 'dark',
      ...extendedCommon,
      ...extendedDark,
      text: {
        // Soft blue-white on deep navy — brand-consistent, never pure white
        primary: '#e9eefb',
        secondary: extendedDark.grey[600],
        disabled: extendedDark.grey[500]
      },
      action: { disabled: extendedDark.grey[400] },
      divider: extendedDark.grey[200],
      background: {
        paper: extendedDark.grey[0],
        default: extendedDark.grey.A50
      }
    }
  };
}
